import { Stack } from '@/components/layout';
import { Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import type { FamilyEvent } from '@/features/guardian/family-events';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

const strings = STRINGS.guardian.kids.eventDetail;

type EventDetailSummaryCardProps = {
  event: FamilyEvent;
};

export function EventDetailSummaryCard({ event }: EventDetailSummaryCardProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  const isTournament = event.type === 'tournament';
  const statusLabel =
    event.status === 'registered'
      ? strings.statusRegistered
      : event.status === 'cancelled'
        ? strings.statusCancelled
        : strings.statusBooked;

  return (
    <Stack
      gap={scale(10)}
      style={[styles.card, isTournament ? styles.tournamentAccent : styles.practiceAccent]}>
      <Text variant="Font Guardian App/Body/Body 16 Bold" style={styles.title}>
        {event.eventName}
      </Text>
      <Stack direction="row" align="center" wrap="wrap" gap={scale(8)}>
        <Stack
          direction="row"
          align="center"
          style={[styles.badge, isTournament ? styles.tournamentBadge : styles.practiceBadge]}>
          <Text
            variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
            style={isTournament ? styles.tournamentText : styles.practiceText}>
            {`✓ ${statusLabel}`}
          </Text>
        </Stack>
        <Stack style={[styles.badge, isTournament ? styles.tournamentBadge : styles.practiceBadge]}>
          <Text
            variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
            style={isTournament ? styles.tournamentText : styles.practiceText}>
            {event.eventTypeLabel}
          </Text>
        </Stack>
      </Stack>
    </Stack>
  );
}

const useStyles = createStyles(({ isTablet, scale }) => ({
  card: {
    padding: scale(12),
    borderRadius: scale(12),
    borderCurve: 'continuous',
    borderLeftWidth: scale(4),
    backgroundColor: isTablet ? guardianColors['1'] : colors['0'],
    boxShadow: '0px 1px 3px #D5D9E24D',
  },
  practiceAccent: {
    borderLeftColor: guardianColors['Event/Practice'],
  },
  tournamentAccent: {
    borderLeftColor: guardianColors['Event/Tournament'],
  },
  title: {
    color: guardianColors['10'],
  },
  badge: {
    paddingHorizontal: scale(12),
    paddingVertical: scale(7),
    borderRadius: scale(999),
    borderCurve: 'continuous',
  },
  practiceBadge: {
    backgroundColor: guardianColors['Event/Practice Background'],
  },
  tournamentBadge: {
    backgroundColor: guardianColors['Event/Tournament Background'],
  },
  practiceText: {
    color: guardianColors['Event/Practice'],
  },
  tournamentText: {
    color: guardianColors['Event/Tournament'],
  },
}));
